import { router } from "@inertiajs/react";
import { useBilling } from "@/contexts/BillingContext";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { BillingFeatures, BillingLimits } from "@/types";

interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  feature?: keyof BillingFeatures;
  limitKey?: keyof BillingLimits;
  orgSlug: string;
}

const PLAN_DESCRIPTIONS: Record<string, string> = {
  free: "Calendario y servicios para empezar sin fricción.",
  basic: "Pensado para una sola agenda y un solo negocio.",
  pro: "Para consultorios, spas y salones con mayor volumen de citas.",
};

const PLAN_FEATURES: Record<string, string[]> = {
  free: [
    "1 negocio / 1 sucursal",
    "1 staff",
    "Servicios ilimitados",
    "Calendario + agenda manual",
    "Confirmación manual",
  ],
  basic: [
    "1 negocio / 1 sucursal",
    "2 staff",
    "1 número de WhatsApp",
    "Agente de WhatsApp con IA",
    "Recordatorios básicos",
    "400 conversaciones/mes",
  ],
  pro: [
    "Hasta 3 sucursales",
    "Staff ilimitado",
    "1 número de WhatsApp",
    "Agente de WhatsApp con IA",
    "Recordatorios avanzados",
    "600 conversaciones/mes",
    "Soporte prioritario",
  ],
};

function formatPrice(cents: number, currency: string): string {
  const amount = cents / 100;
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
  }).format(amount);
}

function getPlanFeatures(planKey: string): string[] {
  return PLAN_FEATURES[planKey] ?? [];
}

export function UpgradeModal({ open, onClose, orgSlug }: UpgradeModalProps) {
  const { plan: currentPlan, billing } = useBilling();
  const availablePlans = billing?.available_plans ?? [];

  if (!open) return null;

  const handleUpgrade = (plan: "basic" | "pro") => {
    router.post(`/${orgSlug}/billing/checkout`, { plan });
    onClose();
  };

  const handleStartTrial = () => {
    router.post(`/${orgSlug}/billing/start_trial`, undefined, {
      onFinish: () => onClose(),
    });
  };

  const handleManage = () => {
    router.post(`/${orgSlug}/billing/portal`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-card rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">Elige tu plan</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Desbloquea más funciones para hacer crecer tu negocio
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid md:grid-cols-3 gap-4">
            {availablePlans.map((plan) => {
              const isTrialing = billing?.status === "trialing";
              const isCurrent = plan.key === currentPlan;
              const isCurrentTrialing = isCurrent && isTrialing;
              const canUpgrade = !isCurrent && plan.key !== "free";
              const isDowngrade =
                !isTrialing &&
                ((currentPlan === "pro" && plan.key === "basic") ||
                (currentPlan !== "free" && plan.key === "free"));
              const isPopular = plan.key === "basic";
              const featureList = getPlanFeatures(plan.key);

              return (
                <div
                  key={plan.key}
                  className={`relative rounded-lg border p-5 flex flex-col ${
                    isPopular
                      ? "border-primary ring-1 ring-primary"
                      : "border-border"
                  } ${isCurrent ? "bg-muted/50" : ""} ${isCurrentTrialing ? "ring-1 ring-yellow-500/50" : ""}`}
                >
                  {isCurrentTrialing && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-500 text-white text-xs px-2 py-0.5 rounded-full">
                      Tu prueba actual
                    </span>
                  )}
                  {isPopular && !isCurrentTrialing && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                      Popular
                    </span>
                  )}

                  <div className="text-center mb-4">
                    <h3 className="font-semibold text-lg">{plan.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {PLAN_DESCRIPTIONS[plan.key] ?? ""}
                    </p>
                    <div className="mt-2">
                      <span className="text-3xl font-bold">
                        {formatPrice(plan.price_cents, plan.currency)}
                      </span>
                      <span className="text-muted-foreground">/mes</span>
                    </div>
                  </div>

                  <ul className="space-y-2 flex-1">
                    {featureList.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-start gap-2 text-sm"
                      >
                        <Check className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {isCurrentTrialing ? (
                    <Button
                      className="w-full mt-6"
                      onClick={() => handleUpgrade(plan.key as "basic" | "pro")}
                    >
                      Suscribirse
                    </Button>
                  ) : isCurrent ? (
                    <Button variant="outline" className="w-full mt-6" disabled>
                      Plan actual
                    </Button>
                  ) : isDowngrade ? (
                    <Button
                      variant="outline"
                      className="w-full mt-6"
                      onClick={handleManage}
                    >
                      Administrar plan
                    </Button>
                  ) : canUpgrade && plan.key === "basic" && billing?.trial_eligible ? (
                    <Button
                      className="w-full mt-6"
                      onClick={handleStartTrial}
                    >
                      Prueba gratis 30 días
                    </Button>
                  ) : canUpgrade ? (
                    <Button
                      className="w-full mt-6"
                      onClick={() => handleUpgrade(plan.key as "basic" | "pro")}
                    >
                      Actualizar a {plan.name}
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
